import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatDatepickerToggle } from '@angular/material/datepicker';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatInput, MatLabel, MatSuffix } from '@angular/material/input';
import { DatePickerComponent } from 'components/input/date-time-picker/date-picker.component';
import { TimePickerComponent } from 'components/input/date-time-picker/time-picker.component';
import { IconComponent } from 'components/layout/icon/icon.component';
import { AutocompleteOffDirective } from 'directives/autocomplete-off.directive';
import { InputStateDirective } from 'directives/input-state.directive';
import { ValidationDirective } from 'directives/validation.directive';
import { Time } from 'models/Time';
import { InputTypeDateTimeModel } from 'models/generated/Models.InputTypeData.InputTypeDateTime';
import { CustomDatePipe } from 'pipes/custom-date.pipe';
import { TranslatePipe } from 'pipes/translate.pipe';

@Component({
	selector: 'date-time-input',
	templateUrl: 'date-time-input.component.html',
	styleUrls: ['./date-time-input.component.scss'],
	imports: [MatFormField, MatLabel, InputStateDirective, MatInput, FormsModule, ValidationDirective, AutocompleteOffDirective, MatIconButton, MatSuffix, MatIcon, MatDatepickerToggle, IconComponent, DatePickerComponent, TimePickerComponent, TranslatePipe, CustomDatePipe]
})
export class DateTimeInputComponent {

	constructor(private readonly element: ElementRef<HTMLElement>, private readonly customDate: CustomDatePipe) { }

	@ViewChild(NgModel) ngModel?: NgModel;
	@ViewChild('input') input!: ElementRef<HTMLElement>;
	@ViewChild('remove') remove?: ElementRef<HTMLElement>;

	@Input() required = false;
	@Input() class = '';
	@Input() maxDate?: Date;
	@Input() minDate?: Date;
	@Input() suggestedMinDate?: Date;
	@Input() markedDates?: Date[];
	@Input() inline = false;
	@Input() label = '';
	@Input() timeOptional = true;
	@Input() timeRange = [6, 18];

	public displayDatePicker = false;
	private valid = false;
	public validationMessage = '';
	public followUpDate?: Date | null;
	public followUpTime?: Time | null;
	public displayValue = '';
	public hasFocus = false;
	touched = false;

	private _keepFocus = false;
	@Input()
	set keepFocus(value: boolean) {
		this._keepFocus = value;
		if (value) {
			this.displayDatePicker = true;
			this.setFocus(true);
		}
	}
	get keepFocus() { return this._keepFocus; }

	private _value?: { date?: Date, dateOnly: boolean; };
	@Input()
	set value(value: string | { date?: Date, dateOnly: boolean; } | undefined) {
		if (typeof value == 'string') value = JSON.parse(value) as { date: Date, dateOnly: boolean; };
		this._value = value;
		this.formatValue();
		if (!value?.date) return;
		
		this.followUpDate = new Date(value.date);
		if (!value.dateOnly) {
			this.followUpTime = new Time();
			this.followUpTime.hour = this.followUpDate.getHours();
			this.followUpTime.minute = this.followUpDate.getMinutes();
		} else {
			this.followUpTime = undefined;
		}
	}
	get value() { return this._value; }

	@Output() valueChange = new EventEmitter<{ date?: Date, dateOnly: boolean; }>();
	@Output() blur = new EventEmitter();
	@Output() focus = new EventEmitter();

	public clicked(target: EventTarget | null) {
		if (!this.element.nativeElement.contains(target as Node)) this.setFocus(false);
	}

	public focusInput(event: MouseEvent) {
		if ((!this.remove || event.target != this.remove.nativeElement) && this.element.nativeElement.contains(event.target as Node)) {
			event.preventDefault();
			this.setFocus(true);
		}
	}

	private formatValue() {
		if (!this._value)
			this.displayValue = '';
		else
			this.displayValue = this._value.dateOnly ? this.customDate.transform(this._value.date, 'shortDate') : this.customDate.transform(this._value.date);
	}

	public setFocus(hasFocus: boolean) {
		if (hasFocus) this.touched = true;
		if (!hasFocus && this.keepFocus) return;
		this.hasFocus = hasFocus;
		this.displayDatePicker = hasFocus;
		this.validationMessage = '';
		this.focus.emit(hasFocus);
		if (!hasFocus) {
			this.blur.emit(true);
			if (!this.valid) this.validationMessage = 'required';
		} else {
			if (!this.value) {
				this.followUpDate = undefined;
				this.followUpTime = undefined;
			}
		}
	}

	valueChanged() {
		if (this.ngModel) this.ngModel.control.markAsDirty();
		if (!this.followUpDate) return;

		if (!this._value) this._value = new InputTypeDateTimeModel;
		this._value.date = new Date(this.followUpDate);
		this._value.dateOnly = true;
		if (this.followUpTime && this.followUpTime.hour) {
			this._value.date!.setHours(this.followUpTime.hour!);
			this._value.date!.setMinutes(this.followUpTime.minute!);
			this._value.dateOnly = false;
		} 
		this.formatValue();

		this.valueChange.emit(this._value);
	}

	resetInput() {
		if (this.ngModel) this.ngModel.control.markAsDirty();
		this.value = undefined;
		this.formatValue();
		this.valueChange.emit(this.value);
		this.setFocus(true);
	}
}
